import type { ColumnDef } from "@tanstack/react-table";
import { t } from "i18next";
import isNil from "lodash/isNil";

import type { IGitRootAttr, IToeInputAttr, IToeInputData } from "./types";

import { editableBePresentFormatter } from "../formatters/editable-be-present";
import { markSeenFirstTimeBy } from "../utils";
import { filterDate } from "utils/date";
import { formatDate } from "utils/date";
import { formatBoolean, formatStatus, jsxStatus } from "utils/format-helpers";

const isEqualRootId = (root: IGitRootAttr | null, rootId: string): boolean => {
  if (isNil(root)) {
    return rootId === "";
  }

  return root.id === rootId;
};

const formatToeInputData = (toeInputAttr: IToeInputAttr): IToeInputData => ({
  ...toeInputAttr,
  attackedAt: toeInputAttr.attackedAt ?? "-",
  bePresentUntil: toeInputAttr.bePresentUntil ?? "-",
  firstAttackAt: toeInputAttr.firstAttackAt ?? "-",
  id: crypto.randomUUID(),
  markedSeenFirstTimeBy: markSeenFirstTimeBy(toeInputAttr.seenFirstTimeBy),
  rootId: isNil(toeInputAttr.root) ? "" : toeInputAttr.root.id,
  rootNickname: isNil(toeInputAttr.root) ? "" : toeInputAttr.root.nickname,
  seenAt: toeInputAttr.seenAt ?? "-",
});

const columns = (
  canUpdateToeInput: boolean,
  handleUpdateToeInputBePresent: (row: IToeInputData) => Promise<void>,
  isInternal: boolean,
): ColumnDef<IToeInputData>[] => [
  {
    accessorKey: "rootNickname",
    header: t("group.toe.inputs.root"),
    id: "root",
  },
  {
    accessorKey: "component",
    header: t("group.toe.inputs.component"),
  },
  {
    accessorKey: "entryPoint",
    header: t("group.toe.inputs.entryPoint"),
    id: "entry_point",
  },
  {
    accessorFn: (row: IToeInputData): string =>
      formatStatus(row.hasVulnerabilities),
    cell: (cell): JSX.Element => jsxStatus(String(cell.getValue())),
    header: String(t("group.toe.inputs.status")),
    id: "status",
  },
  {
    accessorKey: "seenAt",
    cell: (cell): string => formatDate(cell.getValue<string>()),
    filterFn: filterDate,
    header: t("group.toe.inputs.seenAt"),
    id: "seen_at",
  },
  {
    accessorFn: (row: IToeInputData): string => {
      return formatBoolean(row.bePresent);
    },
    cell: (cell): JSX.Element | string =>
      editableBePresentFormatter(
        canUpdateToeInput && isInternal,
        handleUpdateToeInputBePresent,
        cell.row.original,
      ),
    header: t("group.toe.inputs.bePresent"),
    id: "be_present",
  },
  {
    accessorKey: "attackedAt",
    cell: (cell): string => formatDate(cell.getValue<string>()),
    filterFn: filterDate,
    header: t("group.toe.inputs.attackedAt"),
    id: "attacked_at",
  },
  {
    accessorKey: "attackedBy",
    header: t("group.toe.inputs.attackedBy"),
    id: "attacked_by",
  },
  {
    accessorKey: "firstAttackAt",
    cell: (cell): string => formatDate(cell.getValue<string>()),
    filterFn: filterDate,
    header: t("group.toe.inputs.firstAttackAt"),
    id: "first_attack_at",
  },
  {
    accessorKey: "seenFirstTimeBy",
    header: t("group.toe.inputs.seenFirstTimeBy"),
    id: "seen_first_time_by",
  },
  {
    accessorKey: "bePresentUntil",
    cell: (cell): string => formatDate(cell.getValue<string>()),
    filterFn: filterDate,
    header: t("group.toe.inputs.bePresentUntil"),
    id: "be_present_until",
  },
];

const getTableColumns = (
  isInternal: boolean,
  handleUpdateToeInputBePresent: (row: IToeInputData) => Promise<void>,
  permissions: {
    canGetAttackedAt: boolean;
    canGetAttackedBy: boolean;
    canGetFirstAttackAt: boolean;
    canGetSeenFirstTimeBy: boolean;
    canGetBePresentUntil: boolean;
    canUpdateToeInput: boolean;
  },
): ColumnDef<IToeInputData>[] =>
  columns(
    permissions.canUpdateToeInput,
    handleUpdateToeInputBePresent,
    isInternal,
  ).filter((column): boolean => {
    switch (column.id) {
      case "attacked_at":
        return isInternal && permissions.canGetAttackedAt;
      case "attacked_by":
        return isInternal && permissions.canGetAttackedBy;
      case "first_attack_at":
        return isInternal && permissions.canGetFirstAttackAt;
      case "seen_first_time_by":
        return isInternal && permissions.canGetSeenFirstTimeBy;
      case "be_present_until":
        return isInternal && permissions.canGetBePresentUntil;
      case undefined:
      default:
        return true;
    }
  });

export { formatToeInputData, isEqualRootId, getTableColumns };
